import { useEffect, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";

interface OrderSuccessModalProps {
  open: boolean;
  onClose: () => void;
}

export function OrderSuccessModal({ open, onClose }: OrderSuccessModalProps) {
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    if (open) {
      setTimeout(() => setAnimate(true), 100);
    } else {
      setAnimate(false);
    }
  }, [open]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 bg-background rounded-3xl shadow-2xl w-[90vw] max-w-sm px-8 py-10 flex flex-col items-center text-center data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">
          <DialogPrimitive.Title className="sr-only">Order Placed</DialogPrimitive.Title>

          {/* Animated checkmark circle */}
          <div className="relative mb-6">
            <svg
              viewBox="0 0 120 120"
              className="w-28 h-28"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Outer pulsing ring */}
              <circle
                cx="60"
                cy="60"
                r="54"
                fill="none"
                stroke="#22c55e"
                strokeWidth="4"
                strokeDasharray="339.3"
                strokeDashoffset={animate ? "0" : "339.3"}
                strokeLinecap="round"
                style={{
                  transition: "stroke-dashoffset 0.8s cubic-bezier(0.4,0,0.2,1)",
                  transformOrigin: "center",
                  transform: "rotate(-90deg)",
                  transformBox: "fill-box",
                }}
              />
              {/* Filled green circle */}
              <circle
                cx="60"
                cy="60"
                r="44"
                fill="#22c55e"
                opacity={animate ? 1 : 0}
                style={{ transition: "opacity 0.4s ease 0.3s" }}
              />
              {/* Checkmark */}
              <polyline
                points="38,62 54,78 84,44"
                fill="none"
                stroke="white"
                strokeWidth="6"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="60"
                strokeDashoffset={animate ? "0" : "60"}
                style={{
                  transition: "stroke-dashoffset 0.5s cubic-bezier(0.4,0,0.2,1) 0.6s",
                }}
              />
            </svg>

            {/* Sparkles */}
            {animate && (
              <>
                {[...Array(6)].map((_, i) => (
                  <span
                    key={i}
                    className="absolute w-2 h-2 rounded-full bg-green-400"
                    style={{
                      top: `${50 + 44 * Math.sin((i * Math.PI * 2) / 6 - Math.PI / 2)}%`,
                      left: `${50 + 44 * Math.cos((i * Math.PI * 2) / 6 - Math.PI / 2)}%`,
                      transform: "translate(-50%,-50%)",
                      animation: `sparkle-out 0.6s ease ${0.7 + i * 0.05}s both`,
                    }}
                  />
                ))}
              </>
            )}
          </div>

          <h2 className="text-2xl font-bold text-foreground mb-2">
            Order Placed!
          </h2>
          <p className="text-muted-foreground text-sm leading-relaxed mb-8">
            Your order is placed successfully. We'll send you a confirmation shortly and your jewellery will be on its way!
          </p>

          <button
            onClick={onClose}
            className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 rounded-full transition-colors duration-200"
          >
            Continue Shopping
          </button>

          <style>{`
            @keyframes sparkle-out {
              0% { transform: translate(-50%, -50%) scale(0); opacity: 1; }
              50% { transform: translate(-50%, -50%) scale(1.5); opacity: 1; }
              100% { transform: translate(calc(-50% + var(--tx, 0px)), calc(-50% + var(--ty, 0px))) scale(0); opacity: 0; }
            }
          `}</style>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
